// React Query wiring for assumption generation (step 3).
//
// Generation runs on a streaming Lambda Function URL (like dialog), not the
// API Gateway base URL: a full Bedrock response can take ~40s, past API
// Gateway's 30s cap. We POST to the stream endpoint with the Google bearer
// token (the handler verifies it — no API Gateway authorizer fronts a Function
// URL) and read the NDJSON stream to its 'complete' event via the shared
// `consumeNdjsonStream`, previewing streamed assumptions as they complete. On
// success we invalidate the canvas query (new nodes appear in the list and on
// the graph) and the project query (journey badge updates).

import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import { useConfig } from '@/app/ConfigProvider';
import { useAuth } from '@/components/auth/AuthProvider';
import { consumeNdjsonStream } from '@/services/stream';
import { useStreamPreview } from '@/hooks/use-stream-preview';
import type { StreamedAssumption } from '@/types/api';

export function useGenerateAssumptions() {
  const queryClient = useQueryClient();
  const config = useConfig();
  const { getStreamAuthHeaders } = useAuth();
  const preview = useStreamPreview<StreamedAssumption>('assumptions');

  const mutation = useMutation<Array<Record<string, unknown>>, Error, string>({
    // Keyed so other surfaces can observe in-flight runs (see
    // useAssumptionsRunInFlight), whichever instance fired.
    mutationKey: ['generate-assumptions'],
    mutationFn: async (projectId: string) => {
      preview.reset();
      const data = await consumeNdjsonStream<{ assumptions?: Array<Record<string, unknown>> }>(
        `${config.api.streamUrl}/ai/assumption`,
        { projectId },
        getStreamAuthHeaders(),
        preview.onChunk,
      );
      return data.assumptions ?? [];
    },
    // Returning the invalidations keeps the mutation pending until the canvas
    // refetch lands — previews hand off to the real assumption nodes directly.
    onSuccess: (_data, projectId) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['canvas', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['project', projectId] }),
      ]),
  });

  // Streamed previews are the model's raw proposals; the server normalises
  // before persisting and may drop some (malformed / empty-titled) — render-only.
  return {
    ...mutation,
    streamedAssumptions: preview.items,
    streamStarted: preview.started,
    draftingTitle: preview.draftingTitle,
  };
}

// Whether ANY useGenerateAssumptions instance has a run in flight for this
// project (mutation state is per-instance; this crosses surfaces).
export function useAssumptionsRunInFlight(projectId: string) {
  const pending = useMutationState({
    filters: { mutationKey: ['generate-assumptions'], status: 'pending' },
    select: (m) => m.state.variables as string,
  });
  return pending.includes(projectId);
}
